import { ButtonLink, Flex, Select, TextInput } from '@applyboard/crystal-ui';
import { PersonAddOutlineIcon } from '@applyboard/ui-icons';
import { useState } from 'react';

interface OptionProps {
  label: string;
  value: string;
  disabled?: boolean;
}

const FilterableKeys = ['givenName', 'familyName', 'email', 'userType'] as const;
type FilterableKey = (typeof FilterableKeys)[number];

type UserManagementHeadingProps = {
  canCreateUser: boolean;
  onFilterChange: (searchText: string, filter: string) => void;
};

export function UserManagementHeading({ canCreateUser, onFilterChange }: UserManagementHeadingProps): JSX.Element {
  const [filter, setFilter] = useState<FilterableKey>('givenName');
  const [search, setSearch] = useState('');

  const FILTER_OPTIONS: Array<OptionProps> = [
    {
      label: 'Given Name',
      value: 'givenName',
    },
    {
      label: 'Family Name',
      value: 'familyName',
    },
    {
      label: 'Email',
      value: 'email',
    },
    {
      label: 'User Type',
      value: 'userType',
    },
    {
      label: 'App Access',
      value: 'apps',
      disabled: true,
    },
  ];

  const handleChangeFilter = (newFilter: string) => {
    if (isValidFilterKey(newFilter)) {
      setFilter(newFilter);
      onFilterChange(search, newFilter);
    }
  };

  const handleSearchChange = (newSearch: string) => {
    setSearch(newSearch);
    onFilterChange(newSearch, filter);
  };

  const isValidFilterKey = (filterKey: string): filterKey is FilterableKey => {
    return FilterableKeys.includes(filterKey as FilterableKey);
  };

  return (
    <Flex pt={{ xs: 2, sm: 8 }} justify="end" align="center" gap={5}>
      <TextInput
        disabled={!isValidFilterKey(filter)}
        helpText=""
        aria-label="search"
        intent="secondary"
        onChange={handleSearchChange}
        placeholder="Search"
        size="md"
        type="search"
        value={search}
      />
      <Flex gap={2} align="center">
        <Select
          appearance="native"
          aria-label="filter options"
          onChange={handleChangeFilter}
          value={filter}
          placeholder=""
          children={FILTER_OPTIONS.map((option) => (
            <Select.Option key={option.value} value={option.value} label={option.label} />
          ))}
        />
      </Flex>
      {canCreateUser && (
        <ButtonLink
          href="/profiles/school-users/add"
          leadIcon={PersonAddOutlineIcon}
          intent="primary"
          size="md"
          width="hug"
        >
          Add school user
        </ButtonLink>
      )}
    </Flex>
  );
}
